import React, { memo, useCallback, useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'

import {
  BoxV4,
  MultiSelectV4,
  TextAreaWithChipsV4,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'

import {
  DataType,
  FormField,
  SelectorOptions,
} from '@labrav/react-components/lib/@types/types/forms'
import { get } from 'lodash'
import { MatchString } from '@labrav/common'
import { useStyles } from './PrivateOfferStakeholders.styles'
import { OfferStakeholders } from '../../../../modules/privateOffer/types'
import type { StakeholderUserOption } from '../../../../common/components/ViewStakeholders/stakeholderUserOption'

const HEADING_ICON_STYLE = { width: '16px', height: '16px' } as const

const STAKEHOLDERS_MULTI_TOOLTIP = {
  title: '',
  content:
    'Add team members with access to this Labra account to receive this offer-related event notifications.',
}

const CUSTOM_STAKEHOLDERS_TOOLTIP = {
  title: '',
  content:
    'Add the email addresses of individual who do not have access to the Labra account but would like to be notified about this offer related events.',
}

const CUSTOM_EMAILS_SUPPORTING_TEXT =
  'Please press enter or put comma to add emails.'

export type PrivateOfferStakeholdersProps = {
  hideStakeholders?: boolean
  hideHeading?: boolean
  keyName?: string
  showHelpIcon?: boolean
  users?: StakeholderUserOption[]
  stakeholdersFaqs?: JSX.Element
}

const PrivateOfferStakeholdersInner = ({
  hideStakeholders = false,
  hideHeading = false,
  keyName = 'offerStakeholders',
  showHelpIcon = true,
  users = [],
  stakeholdersFaqs,
}: PrivateOfferStakeholdersProps) => {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<Record<string, unknown>>
  const { classes } = useStyles()
  const offerStakeholders = (values[keyName] as OfferStakeholders[]) || []
  const customOfferStakeholders =
    (values?.customOfferStakeholders as OfferStakeholders[]) || []

  const emailsAndNames = useMemo(
    () =>
      users.map(user => ({
        email: user?.email,
        name: user?.name.trim() ? user.name : user.email,
        firstName: (user?.firstName || '').trim(),
        lastName: (user?.lastName || '').trim(),
      })),
    [users]
  )

  const options: SelectorOptions[] = useMemo(
    () =>
      emailsAndNames.map(({ email, name }) => ({
        value: email,
        label: name,
      })),
    [emailsAndNames]
  )

  const multiSelectData: FormField = useMemo(
    () => ({
      title: 'Select stakeholders',
      placeHolder: 'Select stakeholders',
      humanReadableKey: keyName,
      id: keyName,
      isReadOnly: false,
      isRequired: true,
      defaultValue: undefined,
      dataType: 'multi_enum' as DataType,
      validationType: undefined,
      isCustom: false,
      options: options,
    }),
    [keyName, options]
  )

  const handleInputChangeSelect = useCallback(
    (event: unknown) => {
      const selectedValues: unknown = (
        event as React.ChangeEvent<HTMLInputElement>
      ).target.value
      const selectedEmails = (
        Array.isArray(selectedValues) ? selectedValues : [selectedValues]
      ).filter((value): value is string => typeof value === 'string')

      const normalizeEmail = (value: string) => value.trim().toLowerCase()
      const selectedUsers = selectedEmails
        .map((value: string) => {
          const normalizedValue = normalizeEmail(value)
          return (
            emailsAndNames.find(
              user => normalizeEmail(user.email || '') === normalizedValue
            ) ||
            offerStakeholders.find(
              stakeholder =>
                normalizeEmail(stakeholder.email || '') === normalizedValue
            ) ||
            customOfferStakeholders.find(
              stakeholder =>
                normalizeEmail(stakeholder.email || '') === normalizedValue
            ) || { email: value.trim(), firstName: '', lastName: '', name: '' }
          )
        })
        .filter((user): user is OfferStakeholders => Boolean(user?.email))

      setFieldValue(keyName, selectedUsers)
    },
    [
      customOfferStakeholders,
      emailsAndNames,
      keyName,
      offerStakeholders,
      setFieldValue,
    ]
  )

  const handleTextAreaChange = useCallback(
    (value: string) => {
      const email = value
      if (!email) return
      const existingEmails = offerStakeholders.map(
        stakeholder => stakeholder.email
      )
      const customEmails = customOfferStakeholders.map(
        customEmails => customEmails.email
      )

      if (
        MatchString(existingEmails, value) &&
        MatchString(customEmails, value)
      ) {
        const newStakeholder = { email: email }
        const updatedCustomStakeholders = [
          ...customOfferStakeholders,
          newStakeholder,
        ]
        setFieldValue('customOfferStakeholders', updatedCustomStakeholders)
      }
    },
    [customOfferStakeholders, offerStakeholders, setFieldValue]
  )

  const handleDeleteStakeholderSelect = useCallback(
    (value: string) => {
      const filteredOptions = offerStakeholders.filter(
        option => option.email !== value
      )
      setFieldValue(keyName, filteredOptions)
    },
    [keyName, offerStakeholders, setFieldValue]
  )

  const handleDeleteCustomStakeholder = useCallback(
    (value: string) => {
      const filteredOptions = customOfferStakeholders.filter(
        option => option.email !== value
      )
      setFieldValue('customOfferStakeholders', filteredOptions)
    },
    [customOfferStakeholders, setFieldValue]
  )

  const multiSelectSelectedEmails = useMemo(
    () => offerStakeholders?.map(user => user?.email) || [],
    [offerStakeholders]
  )

  const customEmailsSelected = useMemo(
    () => customOfferStakeholders.map(user => user.email),
    [customOfferStakeholders]
  )

  const multiSelectIsError = !!(
    get(touched, keyName, false) && get(errors, keyName, false)
  )
  const multiSelectErrorText =
    (!!get(touched, keyName, false) && get(errors, keyName, '')) || ''

  const customIsError = !!(
    get(touched, 'customOfferStakeholders', false) &&
    get(errors, 'customOfferStakeholders', false)
  )
  const customHelperText =
    (!!get(touched, 'customOfferStakeholders', false) &&
      get(errors, 'customOfferStakeholders', '')) ||
    ''

  return (
    <div
      data-testid="Stakeholders-section"
      className={hideHeading ? classes.dialog : ''}
    >
      <BoxV4
        heading={hideHeading ? undefined : 'Internal stakeholders'}
        headingIcon={
          hideHeading ? undefined : (
            <FontAwesomeIcon icon={faListCheck} style={HEADING_ICON_STYLE} />
          )
        }
        hideHeading={hideHeading}
        secondaryActions={showHelpIcon ? stakeholdersFaqs : undefined}
      >
        <div className={classes.sectionWrapper}>
          {!hideStakeholders && (
            <div className={classes.sectionContainer}>
              <MultiSelectV4
                field={multiSelectData}
                onChangeSelect={handleInputChangeSelect}
                selectedValues={multiSelectSelectedEmails}
                handleDelete={handleDeleteStakeholderSelect}
                isError={multiSelectIsError}
                errorText={multiSelectErrorText}
                onBlur={handleBlur}
                showInsideInfoIcon={true}
                showInsideInfoIconTooltip={STAKEHOLDERS_MULTI_TOOLTIP}
              />
            </div>
          )}
          <div className={classes.sectionContainer}>
            <div className={classes.widthTextArea}>
              <TextAreaWithChipsV4
                id={'customOfferStakeholders'}
                label="Add stakeholders emails (Optional)"
                selectedValues={customEmailsSelected}
                onChange={handleTextAreaChange}
                handleDelete={handleDeleteCustomStakeholder}
                showSpinner={false}
                isError={customIsError}
                helperText={customHelperText}
                onBlur={handleBlur}
                supportingText={CUSTOM_EMAILS_SUPPORTING_TEXT}
                showInsideInfoIcon={true}
                showInsideInfoIconTooltip={CUSTOM_STAKEHOLDERS_TOOLTIP}
              />
            </div>
          </div>
        </div>
      </BoxV4>
    </div>
  )
}

export const PrivateOfferStakeholders = memo(PrivateOfferStakeholdersInner)
PrivateOfferStakeholders.displayName = 'PrivateOfferStakeholders'
